import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { documentService, taskService } from "@miplatform/api";
import { HTTP_STATUSES, ISSUE_IMAGE_TYPES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  createUUID,
  externalReferenceId,
  getDocumentTypeByContentType,
} from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  description: z.string().min(1, "Task Details are required"),
  issueTypeId: z.string().uuid(),
  repairDescription: z.string().optional(),
});

const DEFAULT_VALUES = {
  description: "",
  repairDescription: "",
  issueTypeId: "",
};

export const useIssueRepairDrawerViewModel = ({ task, issue, onClose }) => {
  const [isIssueResolved, setIsIssueResolved] = useState(false);
  const [isAlreadyResolved, setIsAlreadyResolved] = useState(false);

  const [repairImages, setRepairImages] = useState([]);
  const [issueImages, setIssueImages] = useState([]);

  const stateManager = useComponentState();

  const [issueTypes, setIssueTypes] = useState([]);

  const {
    handleSubmit,
    formState: { isValid },
    control,
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(schema),
  });

  const handleClose = () => {
    onClose();
    reset(DEFAULT_VALUES);
    resetImages();
    setIsIssueResolved(false);
  };

  const resetImages = () => {
    setIssueImages([]);
    setRepairImages([]);
  };

  const onClickYes = () => {
    setIsIssueResolved(true);
  };

  const onClickNo = () => {
    setIsIssueResolved(false);
    setValue("repairDescription", "");
  };

  const getIssueTypes = () => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        const options = response.data?.map((issueType) => {
          return {
            ...issueType,
            label: issueType.name,
            value: issueType.id,
          };
        });
        setIssueTypes(options);
      }
    });
  };

  const createIssue = (data) => {
    stateManager.run(async () => {
      const uuid = createUUID();

      const {
        status,
        data: { modifiedOn, id },
      } = await taskService.attempts.createIssue({
        attemptId: task.attemptId,
        taskId: task.id,
        data: {
          description: data.description,
          id: uuid,
          issueTypeId: data.issueTypeId,
        },
        toastMessageConfig: {
          success: { text: "Issue was created successfully." },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        if (issueImages.length > 0) {
          uploadImages({
            images: issueImages,
            imageType: ISSUE_IMAGE_TYPES.ORIGINATING,
            externalReferenceId: externalReferenceId.getIssueRefId({
              issueId: id,
            }),
          });
        }

        if (isIssueResolved) {
          await taskService.issues.resolve({
            id,
            data: {
              repairDescription: data.repairDescription,
              modifiedOn,
            },
          });
          if (repairImages.length > 0) {
            uploadImages({
              images: repairImages,
              imageType: ISSUE_IMAGE_TYPES.RESOLVING,
              externalReferenceId: externalReferenceId.getIssueRefId({
                issueId: id,
              }),
            });
          }
        }

        handleClose();
      }
    });
  };

  const updateIssue = (data) => {
    stateManager.run(async () => {
      const {
        status,
        data: { modifiedOn },
      } = await taskService.issues.update({
        id: issue.id,
        data: {
          description: data.description,
          repairDescription: data.repairDescription,
          issueTypeId: data.issueTypeId,
          modifiedOn: issue.modifiedOn,
        },
        toastMessageConfig: {
          success: { text: "Issue updated successfully" },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        if (issueImages.length > 0) {
          uploadImages({
            images: issueImages,
            imageType: ISSUE_IMAGE_TYPES.ORIGINATING,
            externalReferenceId: externalReferenceId.getIssueRefId({
              issueId: issue.id,
            }),
          });
        }
        if (!isAlreadyResolved && isIssueResolved) {
          const response = await taskService.issues.resolve({
            id: issue.id,
            data: {
              repairDescription: data.repairDescription,
              modifiedOn,
            },
          });

          if (response.status === HTTP_STATUSES.OK && repairImages.length > 0) {
            uploadImages({
              images: repairImages,
              imageType: ISSUE_IMAGE_TYPES.RESOLVING,
              externalReferenceId: externalReferenceId.getIssueRefId({
                issueId: issue.id,
              }),
            });
          }
        }

        handleClose();
      }
    });
  };

  const deleteImage = ({ file, modifiedOn }) => {
    stateManager.run(async () => {
      return documentService.documents.delete({
        id: file.id || file.documentId,
        modifiedOn: modifiedOn || file.modifiedOn,
      });
    });
  };

  const uploadImages = async ({ images, externalReferenceId }) => {
    const documentResponses = await Promise.all(
      images.map((file) => {
        const params = {
          title: file.name,
          externalReferenceId,
          documentType: getDocumentTypeByContentType(file.type),
        };
        return documentService.documents.create({
          data: file,
          params,
          contentType: file.type,
        });
      })
    );

    return documentResponses;
  };

  const onSubmit = (isUpdating) => () => {
    resetImages();
    handleSubmit(isUpdating ? updateIssue : createIssue)();
  };

  return {
    control,
    isUpdating: !!issue.createdOn,
    isIssueResolved,
    submitDisabled: stateManager.isBusy() || !isValid,
    isAlreadyResolved,
    setIsAlreadyResolved,
    getValues,
    setValue,
    onSubmit,
    onClickYes,
    onClickNo,
    handleClose,
    setRepairImages,
    setIssueImages,
    deleteImage,
    getIssueTypes,
    issueTypes,
  };
};
